/**
 * Produce a canonical key for table name comparison.
 *
 * Mirrors the backend `normalize_table_key()` in `shared/utils/alias.py`.
 * Use this wherever two table identifiers need to be compared for equality.
 *
 * @example
 *   normalizeTableKey("edc_vitals_wide.csv")     // "edc_vitals_wide"
 *   normalizeTableKey("edc-vitals-wide")          // "edc_vitals_wide"
 *   normalizeTableKey("cro-database::patients")   // "patients"
 *   normalizeTableKey("patient data.csv")         // "patient_data"
 *   normalizeTableKey("workbook__Sheet1")         // "workbook__sheet1"
 */
/**
 * Slugify a string to a lowercase underscore-separated identifier.
 *
 * Mirrors the backend `slugify_alias()` in `shared/utils/alias.py`.
 * Uses underscores (not hyphens) so the result matches DuckDB identifier
 * conventions and `sanitize_duckdb_name()` output.
 *
 * @example
 *   slugifyAlias("CRO Source abc")  // "cro_source_abc"
 *   slugifyAlias("My Connector")    // "my_connector"
 */
export function slugifyAlias(value: string): string {
  if (!value) return '';
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

export function normalizeTableKey(name: string): string {
  if (!name) return '';

  // Strip connector namespace prefix (e.g. "cro-database::patients" → "patients")
  const colonIdx = name.indexOf('::');
  if (colonIdx >= 0) {
    name = name.slice(colonIdx + 2);
  }

  // Strip file extension (only known data file extensions)
  const dotIdx = name.lastIndexOf('.');
  if (dotIdx > 0) {
    const ext = name.slice(dotIdx).toLowerCase();
    if (['.csv', '.xlsx', '.xls', '.json', '.parquet', '.tsv'].includes(ext)) {
      name = name.slice(0, dotIdx);
    }
  }

  // Extract basename if path-like
  const slashIdx = name.lastIndexOf('/');
  if (slashIdx >= 0) {
    name = name.slice(slashIdx + 1);
  }

  // Replace hyphens and spaces with underscores, lowercase, trim underscores
  return name
    .replace(/[-\s]/g, '_')
    .toLowerCase()
    .replace(/^_+|_+$/g, '');
}
